import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Prompt } from "react-router-dom";
import { format } from "date-fns";
import Admin from "../layout/Admin";
import spinner from "../layout/spinner.gif";
import { getPlan, savePlan } from "../../actions/plan";
import { money, count } from "../tenant/tenantLabels";
import { secondaryButton } from "../template/templateLabels";
import PlanForm from "./PlanForm";
import TargetBar from "./TargetBar";

// The income the kiosks must bring a month and a day for the costs to be paid, beside
// what they bring this month. The backend works all of it out (GET /api/plan, its
// utils/plan.js) from the costs saved in the form at the bottom; saving answers with
// the page worked out again.

// A backend without /api/plan answers with the admin's index.html and a 200, so
// anything but the report counts as not loaded
const isPlan = (value) =>
  Boolean(value) &&
  typeof value === "object" &&
  Boolean(value.settings) &&
  Boolean(value.levels) &&
  Boolean(value.month);

const card = "bg-white border border-gray-100 rounded-xl shadow-xs dark:bg-gray-800 dark:border-gray-700";
const num = "px-4 py-3 text-right tabular-nums";

// null is a number there is nothing to work out from yet
const amount = (value) => (value === null ? "—" : money(value));
const number = (value) => (value === null ? "—" : count(value));

// How much of a target is reached, or null without one
const shareOf = (value, target) => (value !== null && target > 0 ? value / target : null);
const percent = (share) => (share === null ? "—" : `${Math.round(share * 100)}%`);

const StatCard = ({ title, value, note, share }) => (
  <div className={"p-5 " + card}>
    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{title}</p>
    <p className="mt-2 text-2xl font-semibold tracking-tight text-gray-800 truncate dark:text-gray-100">
      {value}
    </p>
    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{note}</p>
    {share !== null && share !== undefined && (
      <div className="mt-3">
        <TargetBar share={share} />
      </div>
    )}
  </div>
);

const SectionHead = ({ title, text }) => (
  <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
    <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100">{title}</h2>
    <p className="text-sm text-gray-500 dark:text-gray-400">{text}</p>
  </div>
);

const headRow =
  "text-xs font-semibold tracking-wide text-left text-gray-500 uppercase bg-gray-50 dark:text-gray-400 dark:bg-gray-900";

// What is still missing a day at the month's pace, in words
const gapOf = (pace, target) => {
  if (!pace || target.month <= 0) return null;
  if (pace.day >= target.day) return "Одоогийн хурдаар энэ сарын зорилтоо давна.";
  const payments =
    target.payments !== null && target.payments > pace.payments
      ? `, ойролцоогоор ${count(Math.round(target.payments - pace.payments))} гүйлгээ`
      : "";
  return `Зорилтод хүрэхэд өдөрт ${money(target.day - pace.day)}${payments} дутуу байна.`;
};

const Plan = ({ getPlan, savePlan }) => {
  // loading | ready | failed
  const [state, setState] = useState("loading");
  const [plan, setPlan] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  // Whether the form has changes it has not saved
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setState("loading");
    const loaded = await getPlan();
    if (!isPlan(loaded)) {
      setState("failed");
      return;
    }
    setPlan(loaded);
    setUpdatedAt(new Date());
    setState("ready");
  }, [getPlan]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (body) => {
    const result = await savePlan(body);
    if (!isPlan(result.plan)) return;
    setPlan(result.plan);
    setUpdatedAt(new Date());
  };

  const head = (
    <div className="flex flex-col mt-6 mb-6 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h1 className="text-2xl font-semibold text-gray-700 dark:text-gray-200">Орлогын зорилт</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Зардлаа нөхөхийн тулд сард, өдөрт хэдийг олох ёстой, одоо хэдийг олж байгаа
        </p>
      </div>
      {updatedAt && (
        <p className="mt-3 text-xs text-gray-400 sm:mt-0 dark:text-gray-500">
          Шинэчилсэн: {format(updatedAt, "HH:mm")}
        </p>
      )}
    </div>
  );

  if (state !== "ready") {
    return (
      <Admin>
        <div className="min-w-0 pb-10">
          {head}
          {state === "loading" ? (
            <img
              src={spinner}
              className="my-6"
              style={{ width: "50px", margin: "auto", display: "block" }}
              alt="Loading..."
            />
          ) : (
            <div className={"flex flex-wrap items-center justify-between gap-4 p-5 " + card}>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                <i className="mr-2 text-red-500 fas fa-exclamation-circle" />
                Орлогын зорилтыг ачаалж чадсангүй
              </p>
              <button type="button" className={secondaryButton} onClick={load}>
                Дахин оролдох
              </button>
            </div>
          )}
        </div>
      </Admin>
    );
  }

  const { settings, budget, levels, month } = plan;
  const { pace, breakEven, target } = levels;
  const hasTarget = target.month > 0;
  const gap = gapOf(pace, target);

  const dayNote = [
    target.payments !== null && `≈ ${count(target.payments)} гүйлгээ`,
    target.kioskDay !== null && `киоск тутамд ${money(target.kioskDay)}`,
  ]
    .filter(Boolean)
    .join(" · ");

  const levelRows = [
    {
      key: "pace",
      label: "Одоогийн хурд",
      note: pace
        ? `Өнгөрсөн ${month.pastDays} өдрийн дундаж сарын эцэс хүртэл үргэлжилбэл`
        : "Сарын эхний өдөр: дундаж хараахан гараагүй",
      level: pace,
    },
    { key: "breakEven", label: "Алдагдалгүй", note: "Зардлаа яг нөхөх орлого", level: breakEven },
    { key: "target", label: "Зорилт", note: "Зардал болон ашгаа нөхөх орлого", level: target, strong: true },
  ];

  return (
    <Admin>
      <Prompt when={dirty} message="Хадгалаагүй өөрчлөлт байна. Хуудаснаас гарах уу?" />
      {/* min-w-0: the layout's grid would otherwise widen the page to the tables'
          width on a phone, instead of letting them scroll */}
      <div className="min-w-0 pb-10">
        {head}

        {!hasTarget && (
          <div className="px-4 py-3 mb-6 text-sm text-purple-700 border border-purple-200 rounded-lg bg-purple-50 dark:text-purple-200 dark:bg-gray-800 dark:border-purple-800">
            <i className="mr-2 fas fa-info-circle" />
            Доорх «Тооцооны үндэслэл» хэсэгт сарын зардлаа оруулж хадгалмагц зорилт гарна.
          </div>
        )}

        <div className="grid gap-4 mb-6 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard
            title="Сарын зорилт"
            value={money(target.month)}
            note={`Алдагдалгүй гарахад ${money(breakEven.month)}`}
          />
          <StatCard
            title="Өдрийн зорилт"
            value={money(target.day)}
            note={dayNote || `${month.daysInMonth} хоногтой сараар`}
          />
          <StatCard
            title={`Энэ сар · ${month.from} — ${month.today}`}
            value={money(month.income)}
            note={
              `Зорилтын ${percent(shareOf(month.income, target.month))} · ${count(month.payments)} гүйлгээ` +
              // Page bundles sold on the site are in the income, with no kiosk payment
              (month.bundleIncome > 0 ? ` · багц ${money(month.bundleIncome)}` : "")
            }
            share={shareOf(month.income, target.month)}
          />
          <StatCard
            title="Өнөөдөр"
            value={money(month.todayIncome)}
            note={`Өдрийн зорилтын ${percent(shareOf(month.todayIncome, target.day))} · ${count(month.todayPayments)} гүйлгээ`}
            share={shareOf(month.todayIncome, target.day)}
          />
        </div>

        <section className={"mb-6 overflow-hidden " + card}>
          <SectionHead
            title="Хэдийг олох ёстой вэ"
            text="Орлоготой сарын тооцоо. Гүйлгээний тоог энэ сарын нэг гүйлгээний дундаж дүнгээр бодсон."
          />
          <div className="w-full overflow-x-auto">
            <table className="w-full whitespace-nowrap">
              <thead>
                <tr className={headRow}>
                  <th className="px-4 py-3">Түвшин</th>
                  <th className="px-4 py-3 text-right">Сард</th>
                  <th className="px-4 py-3 text-right">Өдөрт</th>
                  <th className="px-4 py-3 text-right">Киоск тутамд сард</th>
                  <th className="px-4 py-3 text-right">Киоск тутамд өдөрт</th>
                  <th className="px-4 py-3 text-right">Өдөрт гүйлгээ</th>
                </tr>
              </thead>
              <tbody className="text-sm text-gray-700 divide-y divide-gray-100 dark:text-gray-300 dark:divide-gray-700">
                {levelRows.map(({ key, label, note, level, strong }) => (
                  <tr key={key} className={strong ? "font-semibold text-gray-900 dark:text-white" : ""}>
                    <td className="px-4 py-3">
                      <p className={strong ? "" : "font-medium text-gray-800 dark:text-gray-100"}>{label}</p>
                      <p className="text-xs font-normal text-gray-500 dark:text-gray-400">{note}</p>
                    </td>
                    <td className={num}>{level ? amount(level.month) : "—"}</td>
                    <td className={num}>{level ? amount(level.day) : "—"}</td>
                    <td className={num}>{level ? amount(level.kioskMonth) : "—"}</td>
                    <td className={num}>{level ? amount(level.kioskDay) : "—"}</td>
                    <td className={num}>{level ? number(level.payments) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {gap && (
            <p className="px-5 py-3 text-sm text-gray-600 border-t border-gray-100 dark:text-gray-300 dark:border-gray-700">
              {gap}
            </p>
          )}
        </section>

        <section className={"mb-6 overflow-hidden " + card}>
          <SectionHead
            title="Киоскоор"
            text={
              pace
                ? `Энэ сард орлоготой киоскууд. Өдрийн дундаж нь өнгөрсөн ${month.pastDays} өдрийнх, зорилт нь киоск тутмын өдрийн зорилт.`
                : "Энэ сард орлоготой киоскууд. Сарын эхний өдөр тул өдрийн дундаж хараахан гараагүй."
            }
          />
          {month.kiosks.length === 0 ? (
            <p className="px-5 py-10 text-sm text-center text-gray-500 dark:text-gray-400">
              Энэ сард төлбөртэй гүйлгээ алга.
            </p>
          ) : (
            <div className="w-full overflow-x-auto">
              <table className="w-full whitespace-nowrap">
                <thead>
                  <tr className={headRow}>
                    <th className="px-4 py-3">Киоск</th>
                    <th className="px-4 py-3 text-right">Энэ сарын орлого</th>
                    <th className="px-4 py-3 text-right">Гүйлгээ</th>
                    <th className="px-4 py-3 text-right">Өдрийн дундаж</th>
                    <th className="px-4 py-3 text-right">Зорилтын хувь</th>
                  </tr>
                </thead>
                <tbody className="text-sm text-gray-700 divide-y divide-gray-100 dark:text-gray-300 dark:divide-gray-700">
                  {month.kiosks.map((kiosk) => {
                    const share = shareOf(kiosk.dailyAverage, target.kioskDay);
                    return (
                      <tr key={kiosk.id}>
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-800 dark:text-gray-100">{kiosk.name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {kiosk.location || "Байршилгүй"}
                          </p>
                        </td>
                        <td className={num}>{money(kiosk.income)}</td>
                        <td className={num}>{count(kiosk.payments)}</td>
                        <td className={num}>{amount(kiosk.dailyAverage)}</td>
                        <td className={num}>
                          {percent(share)}
                          {share !== null && (
                            <div className="w-24 mt-1 ml-auto">
                              <TargetBar share={share} />
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {month.bundleIncome > 0 && (
                    <tr className="text-sm text-purple-700 dark:text-purple-300">
                      <td className="px-4 py-3">Вэбээр зарсан багц · {count(month.bundleCount)}</td>
                      <td className={num}>{money(month.bundleIncome)}</td>
                      <td className={num}>—</td>
                      <td className={num}>—</td>
                      <td className={num}>—</td>
                    </tr>
                  )}
                </tbody>
                <tfoot>
                  <tr className="text-sm font-semibold text-gray-800 bg-gray-50 dark:text-gray-100 dark:bg-gray-900">
                    <td className="px-4 py-3">Нийт</td>
                    <td className={num}>{money(month.income)}</td>
                    <td className={num}>{count(month.payments)}</td>
                    <td className={num}>{pace ? money(pace.day) : "—"}</td>
                    <td className={num}>{percent(shareOf(pace ? pace.day : null, target.day))}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </section>

        <div className="grid gap-4 lg:grid-cols-3">
          <section className={"overflow-hidden self-start " + card}>
            <SectionHead title="Зорилтот сарын төсөв" text="Сарын зорилтот орлого хаашаа явах вэ" />
            <dl className="px-5 py-2 text-sm text-gray-700 divide-y divide-gray-100 dark:text-gray-300 dark:divide-gray-700">
              {settings.costs.map((cost, index) => (
                <div key={index} className="flex justify-between gap-4 py-2">
                  <dt>{cost.name}</dt>
                  <dd className="tabular-nums">{money(cost.amount)}</dd>
                </div>
              ))}
              <div className="flex justify-between gap-4 py-2">
                <dt>Хувьсах зардал ({settings.variablePercent}%)</dt>
                <dd className="tabular-nums">{money(budget.variable)}</dd>
              </div>
              <div className="flex justify-between gap-4 py-2">
                <dt>Орлогогүй саруудын нөөц</dt>
                <dd className="tabular-nums">{money(budget.reserve)}</dd>
              </div>
              <div className="flex justify-between gap-4 py-2">
                <dt>Ашиг</dt>
                <dd className="tabular-nums">{money(budget.profit)}</dd>
              </div>
              <div className="flex justify-between gap-4 py-2 font-semibold text-gray-900 dark:text-white">
                <dt>Нийт</dt>
                <dd className="tabular-nums">{money(target.month)}</dd>
              </div>
            </dl>
          </section>

          <section className={"overflow-hidden lg:col-span-2 " + card}>
            <SectionHead
              title="Тооцооны үндэслэл"
              text="Эдгээрийг өөрчилж хадгалахад дээрх зорилтууд дахин бодогдоно"
            />
            <PlanForm settings={settings} onSave={save} onDirty={setDirty} />
          </section>
        </div>
      </div>
    </Admin>
  );
};

StatCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  note: PropTypes.string.isRequired,
  share: PropTypes.number,
};
SectionHead.propTypes = {
  title: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
};
Plan.propTypes = {
  getPlan: PropTypes.func.isRequired,
  savePlan: PropTypes.func.isRequired,
};

export default connect(null, { getPlan, savePlan })(Plan);
